import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'

interface MigrationChartProps {
  data: {
    local: number
    cantonal: number
    interCantonal: number
    international: number
    newBorn: number
  }
  height?: number
  t: TFunction
}

export interface MigrationChartRef {
  exportChart: () => string | null
}

export const MigrationChart = forwardRef<
  MigrationChartRef,
  MigrationChartProps
>(({ data, height = 300, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Prepare migration data
    const totalMigration =
      data.local +
      data.cantonal +
      data.interCantonal +
      data.international +
      data.newBorn
    const migrationData = [
      {
        name: t('charts.migrationChart.legend.local'),
        value: data.local,
        percentage:
          totalMigration > 0
            ? ((data.local / totalMigration) * 100).toFixed(1)
            : '0',
        description: t('charts.migrationChart.descriptions.local'),
      },
      {
        name: t('charts.migrationChart.legend.cantonal'),
        value: data.cantonal,
        percentage:
          totalMigration > 0
            ? ((data.cantonal / totalMigration) * 100).toFixed(1)
            : '0',
        description: t('charts.migrationChart.descriptions.cantonal'),
      },
      {
        name: t('charts.migrationChart.legend.interCantonal'),
        value: data.interCantonal,
        percentage:
          totalMigration > 0
            ? ((data.interCantonal / totalMigration) * 100).toFixed(1)
            : '0',
        description: t('charts.migrationChart.descriptions.interCantonal'),
      },
      {
        name: t('charts.migrationChart.legend.international'),
        value: data.international,
        percentage:
          totalMigration > 0
            ? ((data.international / totalMigration) * 100).toFixed(1)
            : '0',
        description: t('charts.migrationChart.descriptions.international'),
      },
      {
        name: t('charts.migrationChart.legend.newBorn'),
        value: data.newBorn,
        percentage:
          totalMigration > 0
            ? ((data.newBorn / totalMigration) * 100).toFixed(1)
            : '0',
        description: t('charts.migrationChart.descriptions.newBorn'),
      },
    ]

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: function (params: any) {
          const item = params.data
          return `
            <div style="text-align: left;">
              <strong>${item.name} ${t('charts.migrationChart.tooltips.origin')}</strong><br/>
              <span style="color: ${params.color};">● ${item.value.toLocaleString()}</span><br/>
              <span style="color: ${params.color};">● ${item.percentage}%</span><br/>
              <span style="color: #6b7280;">${item.description}</span>
            </div>
          `
        },
      },
      legend: {
        orient: 'horizontal',
        left: 'center',
        top: '5%',
        data: [
          t('charts.migrationChart.legend.local'),
          t('charts.migrationChart.legend.cantonal'),
          t('charts.migrationChart.legend.interCantonal'),
          t('charts.migrationChart.legend.international'),
          t('charts.migrationChart.legend.newBorn'),
        ],
        selectedMode: false,
        textStyle: {
          fontSize: 11,
        },
      },
      series: [
        {
          name: t('charts.series.migrationOrigin'),
          type: 'pie',
          radius: ['25%', '55%'],
          center: ['50%', '60%'],
          selectedMode: false,
          data: migrationData.map((item, index) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
            description: item.description,
            itemStyle: {
              color: [
                '#10b981', // Local - green
                '#22c55e', // Cantonal - light green
                '#3b82f6', // Inter-cantonal - blue
                '#8b5cf6', // International - purple
                '#f59e0b', // New born - orange
              ][index],
            },
          })),
          label: {
            formatter: function (params: any) {
              return `${params.data.name}\n${params.data.percentage}%`
            },
            fontSize: 10,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '250px',
      }}
    />
  )
})

MigrationChart.displayName = 'MigrationChart'
